import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  Platform,
  Image,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute, CommonActions } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { DashboardData, StrategicGoalList, KPIList, InitiativeList, DueKPIReading } from '../../types';
import { COLORS, FONTS, API_BASE_URL } from '../../constants/config';
import { EmptyState, PageContainer, Navbar, DashboardSkeleton, LoadingScreen, SKELETON_DIMENSIONS, Breadcrumb, OrganizationalChart, EvalSummaryCard } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationBanner } from '../myorganization/components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive } from '../../hooks';
import { useDashboardQuery, useOrganizationQuery, useDashboardGoalsQuery, useDashboardInitiativesQuery } from '../../hooks/queries';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { schedulesApi, organizationalUnitsApi } from '../../services/apiService';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const SearchIcon = ({ size = 20, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BellIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SunIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 2V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4.93 4.93L6.34 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17.66 17.66L19.07 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6.34 17.66L4.93 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.07 4.93L17.66 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FilterIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 21V14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 10V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 21V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 8V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 21V16" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M1 14H7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 8H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 16H23" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.3751 2.62523C18.7729 2.2274 19.3125 2.00391 19.8751 2.00391C20.4377 2.00391 20.9773 2.2274 21.3751 2.62523C21.7729 3.02305 21.9964 3.56262 21.9964 4.12523C21.9964 4.68784 21.7729 5.2274 21.3751 5.62523L12.3621 14.6392C12.1246 14.8765 11.8313 15.0501 11.5091 15.1442L8.63609 15.9842C8.55005 16.0093 8.45883 16.0108 8.372 15.9886C8.28517 15.9663 8.20592 15.9212 8.14254 15.8578C8.07916 15.7944 8.03398 15.7151 8.01174 15.6283C7.98949 15.5415 7.991 15.4503 8.01609 15.3642L8.85609 12.4912C8.95062 12.1693 9.12463 11.8763 9.36209 11.6392L18.3751 2.62523Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TargetIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Icon: chart-column-big (المؤشرات الاستراتيجية)
const ChartColumnBigIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 3V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 5H16C15.4477 5 15 5.44772 15 6V16C15 16.5523 15.4477 17 16 17H18C18.5523 17 19 16.5523 19 16V6C19 5.44772 18.5523 5 18 5Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 8H8C7.44772 8 7 8.44772 7 9V16C7 16.5523 7.44772 17 8 17H10C10.5523 17 11 16.5523 11 16V9C11 8.44772 10.5523 8 10 8Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Icon: rocket (المبادرات)
const RocketIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4.5 16.4993C3 17.7593 2.5 21.4993 2.5 21.4993C2.5 21.4993 6.24 20.9993 7.5 19.4993C8.21 18.6593 8.2 17.3693 7.41 16.5893C7.02131 16.2184 6.50929 16.004 5.97223 15.9874C5.43516 15.9708 4.91088 16.1531 4.5 16.4993Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15.0002L9 12.0002C9.53214 10.6197 10.2022 9.29631 11 8.05025C12.1652 6.18723 13.7876 4.6533 15.713 3.59434C17.6384 2.53538 19.8027 1.98662 22 2.00025C22 4.72025 21.22 9.50025 16 13.0002C14.7369 13.799 13.3968 14.469 12 15.0002Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11.9995H4C4 11.9995 4.55 8.96953 6 7.99953C7.62 6.91953 11 7.99953 11 7.99953" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V20C12 20 15.03 19.45 16 18C17.08 16.38 16 13 16 13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Icon: goal (الأهداف الاستراتيجية)
const GoalIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 13V2L20 6L12 10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20.5607 10.2216C21.0928 11.8618 21.1426 13.6203 20.7041 15.2879C20.2656 16.9555 19.3573 18.4621 18.0872 19.6283C16.8172 20.7946 15.2388 21.5714 13.5399 21.8665C11.841 22.1615 10.0932 21.9623 8.50424 21.2925C6.91532 20.6228 5.55224 19.5107 4.57717 18.0885C3.6021 16.6664 3.05609 14.9941 3.00409 13.2706C2.9521 11.547 3.39631 9.84486 4.28388 8.36653C5.17145 6.8882 6.46502 5.69595 8.01068 4.93164" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8.00238 9.99609C7.50129 10.6631 7.17506 11.4449 7.05343 12.2703C6.93179 13.0956 7.01862 13.9383 7.30599 14.7215C7.59336 15.5048 8.07214 16.2036 8.69871 16.7545C9.32529 17.3053 10.0797 17.6906 10.8933 17.8753C11.7069 18.0599 12.5538 18.0381 13.3567 17.8117C14.1597 17.5853 14.8933 17.1616 15.4906 16.5792C16.0879 15.9967 16.53 15.2741 16.7766 14.4771C17.0232 13.6801 17.0664 12.8341 16.9024 12.0161" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Icon: badge-check (المبادرات المكتملة)
const BadgeCheckIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3.85019 8.61912C3.70423 7.96165 3.72665 7.27796 3.91535 6.63146C4.10405 5.98496 4.45294 5.39657 4.92966 4.92084C5.40638 4.44512 5.9955 4.09747 6.6424 3.91012C7.2893 3.72277 7.97303 3.70179 8.63019 3.84912C8.9919 3.28342 9.4902 2.81788 10.0791 2.49541C10.6681 2.17293 11.3287 2.00391 12.0002 2.00391C12.6716 2.00391 13.3323 2.17293 13.9212 2.49541C14.5102 2.81788 15.0085 3.28342 15.3702 3.84912C16.0284 3.70114 16.7133 3.72203 17.3612 3.90983C18.0091 4.09764 18.599 4.44626 19.076 4.92327C19.5531 5.40029 19.9017 5.99019 20.0895 6.63812C20.2773 7.28605 20.2982 7.97095 20.1502 8.62912C20.7159 8.99083 21.1814 9.48912 21.5039 10.0781C21.8264 10.667 21.9954 11.3277 21.9954 11.9991C21.9954 12.6706 21.8264 13.3312 21.5039 13.9202C21.1814 14.5091 20.7159 15.0074 20.1502 15.3691C20.2975 16.0263 20.2765 16.71 20.0892 17.3569C19.9018 18.0038 19.5542 18.5929 19.0785 19.0697C18.6027 19.5464 18.0144 19.8953 17.3679 20.084C16.7213 20.2727 16.0377 20.2951 15.3802 20.1491C15.019 20.717 14.5203 21.1845 13.9303 21.5084C13.3404 21.8324 12.6782 22.0022 12.0052 22.0022C11.3322 22.0022 10.67 21.8324 10.0801 21.5084C9.49011 21.1845 8.99143 20.717 8.63019 20.1491C7.97303 20.2965 7.2893 20.2755 6.6424 20.0881C5.9955 19.9008 5.40638 19.5531 4.92966 19.0774C4.45294 18.6017 4.10405 18.0133 3.91535 17.3668C3.72665 16.7203 3.70423 16.0366 3.85019 15.3791C3.28015 15.0184 2.81061 14.5193 2.48524 13.9283C2.15988 13.3374 1.98926 12.6737 1.98926 11.9991C1.98926 11.3245 2.15988 10.6609 2.48524 10.0699C2.81061 9.47895 3.28015 8.97988 3.85019 8.61912Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12L11 14L15 10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CheckCircleIcon = ({ size = 24, color = tokens.colors.raw.green["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 4L12 14.01L9 11.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FlagIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7 22V2L17 7L7 12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrendUpIcon = ({ size = 20, color = tokens.colors.raw.green["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M23 6l-9.5 9.5-5-5L1 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 6h6v6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClockIcon = ({ size = 20, color = tokens.colors.raw.yellow["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6v6l4 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DocumentIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClipboardCheckIcon = ({ size = 24, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5C15 5.53043 14.7893 6.03914 14.4142 6.41421C14.0391 6.78929 13.5304 7 13 7H11C10.4696 7 9.96086 6.78929 9.58579 6.41421C9.21071 6.03914 9 5.53043 9 5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 14L11 16L15 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color = '#2CA02C',
  backgroundColor = tokens.colors.raw.gray["200"]
}: CircularGaugeProps) => {
  const { colors } = useTheme();
  // Add padding to accommodate the dot indicator
  const dotPadding = 10;
  const svgSize = size + dotPadding * 2;
  const adjustedCenter = svgSize / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = adjustedCenter + radius * Math.cos(angle);
  const dotY = adjustedCenter + radius * Math.sin(angle);

  return (
    <View style={{ width: svgSize, height: svgSize, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={svgSize} height={svgSize}>
        {/* Background circle - translucent same color as progress arc */}
        <Circle
          cx={adjustedCenter}
          cy={adjustedCenter}
          r={radius}
          stroke={`${color}33`}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={adjustedCenter}
          cy={adjustedCenter}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${adjustedCenter} ${adjustedCenter})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke={colors.card}
            strokeWidth={2}
          />
        )}
      </Svg>
      {/* Percentage text */}
      <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: svgSize, height: svgSize }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontSize: Math.round(size / 5), fontFamily: FONTS.numbers, fontWeight: '500', color: colors.textPrimary }}
        >
          % {formatPct(percentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Segmented Circular Gauge (for second KPI)
// ============================================================

interface SegmentedGaugeProps {
  actual: number;
  target: number;
  size?: number;
  color?: string;
}

const SegmentedGauge = ({ actual, target, size = 140, color = '#2CA02C' }: SegmentedGaugeProps) => {
  const { colors } = useTheme();
  const strokeWidth = 12;
  const ringBgColor = `${color}33`; // ~20% opacity
  const wedgeFillColor = `${color}73`; // ~45% opacity
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = radius * 2 * Math.PI;

  // Pie = TARGET (backdrop), outer arc + dot = ACTUAL (progress indicator)
  const actualPercentage = Math.min(Math.max(actual, 0), 100);
  const targetPercentage = Math.min(Math.max(target, 0), 100);
  const actualOffset = circumference - (actualPercentage / 100) * circumference;

  // Dot position at end of ACTUAL arc
  const dotAngleRad = -Math.PI / 2 + (actualPercentage / 100) * 2 * Math.PI;
  const dotX = center + radius * Math.cos(dotAngleRad);
  const dotY = center + radius * Math.sin(dotAngleRad);
  const dotRadius = Math.max(3, Math.round(strokeWidth * 0.6));

  // Pie wedge path showing TARGET (filled backdrop area inside the ring)
  const getTargetWedgePath = () => {
    if (targetPercentage <= 0) return '';
    const startAngleRad = -Math.PI / 2;
    const endAngleRad = startAngleRad + (targetPercentage / 100) * 2 * Math.PI;
    const innerR = radius - strokeWidth / 2;
    const x1 = center + innerR * Math.cos(startAngleRad);
    const y1 = center + innerR * Math.sin(startAngleRad);
    const x2 = center + innerR * Math.cos(endAngleRad);
    const y2 = center + innerR * Math.sin(endAngleRad);
    const largeArc = targetPercentage > 50 ? 1 : 0;
    return `M ${center} ${center} L ${x1} ${y1} A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background ring */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Inner pie wedge - TARGET filled backdrop */}
        {targetPercentage > 0 && (
          <Path
            d={getTargetWedgePath()}
            fill={wedgeFillColor}
          />
        )}
        {/* Outer ring arc - ACTUAL progress indicator */}
        {actualPercentage > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={actualOffset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* Dot at end of ACTUAL arc */}
        {actualPercentage > 0 && actualPercentage < 100 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={dotRadius}
            fill={color}
            stroke={colors.card}
            strokeWidth={1.5}
          />
        )}
      </Svg>
      {/* Percentage text - actual */}
      <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontSize: Math.round(size / 5), fontFamily: FONTS.numbers, fontWeight: '500', color: colors.textPrimary }}
        >
          % {formatPct(actual)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Mini Progress Bar Component
// ============================================================

interface MiniProgressBarProps {
  percentage: number;
  color?: string;
  backgroundColor?: string;
}

const MiniProgressBar = ({
  percentage,
  color = tokens.colors.raw.green["500"],
  backgroundColor
}: MiniProgressBarProps) => {
  const { colors } = useTheme();
  const bgColor = backgroundColor || colors.backgroundAlt;
  return (
    <View style={{ height: 8, backgroundColor: bgColor, borderRadius: tokens.radii.absolute["radius-4"], overflow: 'hidden', flex: 1 }}>
      <View
        style={{
          height: '100%',
          width: `${Math.min(percentage, 100)}%`,
          backgroundColor: color,
          borderRadius: tokens.radii.absolute["radius-4"],
        }}
      />
    </View>
  );
};

// ============================================================
// Small Donut Gauge Component (for goal headers)
// ============================================================

interface DonutGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

const DonutGauge = ({
  percentage,
  size = 48,
  strokeWidth = 5,
  color = tokens.colors.raw.green["500"]
}: DonutGaugeProps) => {
  const { colors } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={colors.border}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </Svg>
    </View>
  );
};

// ============================================================
// KPI Card Component (inside goal sections)
// ============================================================

interface KPICardProps {
  id?: number;
  title: string;
  actual: number | string;
  target: number | string;
  status: 'on_track' | 'at_risk' | 'behind';
  onViewDetails?: (kpiId: number) => void;
}

const KPICard = ({
  id,
  title,
  actual,
  target,
  status,
  onViewDetails
}: KPICardProps) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const getStatusColor = () => {
    switch (status) {
      case 'on_track': return '#2CA02C';
      case 'at_risk': return tokens.colors.raw.orange["500"];
      case 'behind': return tokens.colors.raw.red["500"];
      default: return '#2CA02C';
    }
  };

  const statusColor = getStatusColor();
  const percentage = typeof actual === 'number' && typeof target === 'number' && target > 0
    ? parseFloat(((actual / target) * 100).toFixed(2))
    : 0;

  // Format value display based on type (percentage vs count)
  const displayValue = typeof actual === 'number' && actual <= 100 && target === 100
    ? `% ${actual}`
    : `${target}\\${actual}`;

  return (
    <View style={[cardStyles.kpiCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* KPI Title */}
      <Text style={[cardStyles.kpiCardTitle, { color: colors.textPrimary }]}>{title}</Text>

      {/* Value and Progress Row */}
      <View style={cardStyles.kpiValueRow}>
        {/* Value Badge */}
        <View style={[cardStyles.kpiValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[cardStyles.kpiValue, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        {/* Progress Bar with Dot - RTL (fills from right to left) */}
        <View style={cardStyles.kpiProgressContainer}>
          <View style={[cardStyles.kpiProgressTrack, { backgroundColor: colors.backgroundAlt }]}>
            <View style={[cardStyles.kpiProgressFill, { width: `${percentage}%`, backgroundColor: statusColor }]} />
            <View style={[cardStyles.kpiProgressDot, { backgroundColor: statusColor, start: `${100 - percentage}%`, transform: [{ translateX: -6 }], borderColor: colors.card }]} />
          </View>
        </View>
      </View>

      {/* View Details Link */}
      <TouchableOpacity
        style={cardStyles.kpiDetailsLink}
        onPress={() => id && onViewDetails?.(id)}
        disabled={!id}
      >
        <ChevronLeftIcon size={16} color={id ? colors.iconDefault : colors.textMuted} />
        <Text style={[cardStyles.kpiDetailsText, { color: id ? colors.textSecondary : colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Goal Section Component (Figma Design - Same as Initiative KPIs)
// ============================================================

interface GoalSectionProps {
  goalId: number;
  title: string;
  percentage: number;
  status: 'on_track' | 'at_risk' | 'behind';
  kpis: Array<{
    id?: number;
    title: string;
    achieved: number | string;
    target: number | string;
    unit: string;
    achievementPercentage: number;
  }>;
  onViewDetails?: (goalId: number) => void;
  onKPIViewDetails?: (kpiId: number) => void;
}

const GoalSection = ({
  goalId,
  title,
  percentage,
  status,
  kpis,
  onViewDetails,
  onKPIViewDetails
}: GoalSectionProps) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  // Calculate color based on percentage (same as InitiativeKPIRow)
  const getPercentageColor = () => {
    if (percentage >= 80) return '#2CA02C';
    if (percentage >= 50) return tokens.colors.raw.orange["500"];
    return tokens.colors.raw.red["500"];
  };

  const percentageColor = getPercentageColor();

  return (
    <View style={[cardStyles.initiativeKPIRow, { backgroundColor: colors.backgroundAlt }]}>
      {/* Goal Header */}
      <View style={cardStyles.initiativeKPIHeader}>
        {/* Left: Button + Percentage */}
        <View style={cardStyles.initiativeKPILeftGroup}>
          <TouchableOpacity
            style={[cardStyles.initiativeKPIViewDetailsBtn, { borderColor: colors.border, backgroundColor: colors.card }]}
            onPress={() => onViewDetails?.(goalId)}
          >
            <EyeIcon size={16} color={colors.iconMuted} />
            <Text style={[cardStyles.initiativeKPIViewDetailsBtnText, { color: colors.textSecondary }]}>{t('common.viewDetails')}</Text>
          </TouchableOpacity>

          <View style={cardStyles.initiativeKPIPercentageGroup}>
            <DonutGauge percentage={percentage} size={32} strokeWidth={4} color={percentageColor} />
            <Text style={[cardStyles.initiativeKPIPercentageText, { color: colors.textPrimary }]}>%{percentage}</Text>
          </View>
        </View>

        {/* Right: Goal Title with Bullet */}
        <View style={cardStyles.initiativeKPITitleContainer}>
          <Text style={[cardStyles.initiativeKPIRowTitle, { color: colors.textPrimary }]}>{title}</Text>
          <View style={cardStyles.initiativeKPIBullet} />
        </View>
      </View>

      {/* KPI Cards Grid - indicator layout, same as initiative/process KPIs */}
      <View style={cardStyles.initiativeKPICardsGrid}>
        {kpis.map((kpi, index) => {
          // Status + bar follow the engine achievement % (matches the donut)
          const achievement = kpi.achievementPercentage;
          const status: 'on_track' | 'at_risk' | 'behind' =
            achievement >= 80 ? 'on_track' : achievement >= 50 ? 'at_risk' : 'behind';
          return (
            <InitiativeKPICard
              key={kpi.id || index}
              id={kpi.id}
              title={kpi.title}
              actual={kpi.achieved}
              target={kpi.target}
              status={status}
              percentage={achievement}
              valueFormat="percentage"
              onViewDetails={onKPIViewDetails}
            />
          );
        })}
      </View>
    </View>
  );
};

// ============================================================
// Strategic Goals Section Header (Figma Design - Same as Initiative KPIs)
// ============================================================

const StrategicGoalsSectionHeader = ({ overallPercentage }: { overallPercentage: number }) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const gaugeColor = overallPercentage >= 80 ? '#2CA02C' : overallPercentage >= 50 ? tokens.colors.raw.orange["500"] : tokens.colors.raw.red["500"];
  return (
    <View style={cardStyles.initiativeKPIsSectionHeader}>
      {/* Left: Overall Percentage with circular gauge */}
      <View style={cardStyles.initiativeKPIsPercentageContainer}>
        <DonutGaugeWithPercentage percentage={overallPercentage} size={48} strokeWidth={5} color={gaugeColor} />
      </View>

      {/* Right: Title with Target Icon */}
      <View style={cardStyles.initiativeKPIsTitleRow}>
        <Text style={[cardStyles.initiativeKPIsSectionTitle, { color: colors.textPrimary }]}>{t('dashboard.strategicGoalsKPIs')}</Text>
        <TargetIcon size={40} color={colors.iconDefault} />
      </View>
    </View>
  );
};

// ============================================================
// Initiative KPIs Section Header (Figma Design)
// ============================================================

const InitiativeKPIsSectionHeader = ({ overallPercentage }: { overallPercentage: number }) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const gaugeColor = overallPercentage >= 80 ? '#2CA02C' : overallPercentage >= 50 ? tokens.colors.raw.orange["500"] : tokens.colors.raw.red["500"];
  return (
    <View style={cardStyles.initiativeKPIsSectionHeader}>
      {/* Left: Overall Percentage with circular gauge */}
      <View style={cardStyles.initiativeKPIsPercentageContainer}>
        <DonutGaugeWithPercentage percentage={overallPercentage} size={48} strokeWidth={5} color={gaugeColor} />
      </View>

      {/* Right: Title with Rocket Icon */}
      <View style={cardStyles.initiativeKPIsTitleRow}>
        <Text style={[cardStyles.initiativeKPIsSectionTitle, { color: colors.textPrimary }]}>{t('dashboard.initiativeKPIs')}</Text>
        <RocketIcon size={40} color={colors.iconDefault} />
      </View>
    </View>
  );
};

// Donut gauge with percentage text inside (for header)
const DonutGaugeWithPercentage = ({
  percentage,
  size = 48,
  strokeWidth = 5,
  color = tokens.colors.raw.orange["500"]
}: DonutGaugeProps) => {
  const { colors } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={colors.border}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontSize: Math.max(9, Math.round(size / 5)), fontFamily: FONTS.numbers, fontWeight: '500', color: colors.textPrimary }}
        >
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Initiative Row with KPIs (Figma Design)
// ============================================================

interface InitiativeKPIRowProps {
  initiativeId: number;
  title: string;
  percentage: number;
  kpis: Array<{
    id?: number;
    title: string;
    actual: number | string;
    target: number | string;
    status: 'on_track' | 'at_risk' | 'behind';
    achievementPercentage?: number;
  }>;
  onViewDetails?: (initiativeId: number) => void;
  onKPIViewDetails?: (kpiId: number) => void;
}

const InitiativeKPIRow = ({
  initiativeId,
  title,
  percentage,
  kpis,
  onViewDetails,
  onKPIViewDetails
}: InitiativeKPIRowProps) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  // Calculate color based on percentage
  const getPercentageColor = () => {
    if (percentage >= 80) return '#2CA02C';
    if (percentage >= 50) return tokens.colors.raw.orange["500"];
    return tokens.colors.raw.red["500"];
  };

  const percentageColor = getPercentageColor();

  return (
    <View style={[cardStyles.initiativeKPIRow, { backgroundColor: colors.backgroundAlt }]}>
      {/* Initiative Header */}
      <View style={cardStyles.initiativeKPIHeader}>
        {/* Left: Button + Percentage */}
        <View style={cardStyles.initiativeKPILeftGroup}>
          <TouchableOpacity
            style={[cardStyles.initiativeKPIViewDetailsBtn, { borderColor: colors.border, backgroundColor: colors.card }]}
            onPress={() => onViewDetails?.(initiativeId)}
          >
            <EyeIcon size={16} color={colors.iconMuted} />
            <Text style={[cardStyles.initiativeKPIViewDetailsBtnText, { color: colors.textSecondary }]}>{t('common.viewDetails')}</Text>
          </TouchableOpacity>

          <View style={cardStyles.initiativeKPIPercentageGroup}>
            <DonutGauge percentage={percentage} size={32} strokeWidth={4} color={percentageColor} />
            <Text style={[cardStyles.initiativeKPIPercentageText, { color: colors.textPrimary }]}>%{percentage}</Text>
          </View>
        </View>

        {/* Right: Initiative Title with Bullet */}
        <View style={cardStyles.initiativeKPITitleContainer}>
          <Text style={[cardStyles.initiativeKPIRowTitle, { color: colors.textPrimary }]}>{title}</Text>
          <View style={cardStyles.initiativeKPIBullet} />
        </View>
      </View>

      {/* KPI Cards Grid */}
      <View style={cardStyles.initiativeKPICardsGrid}>
        {kpis.map((kpi, index) => (
          <InitiativeKPICard
            key={kpi.id || index}
            id={kpi.id}
            title={kpi.title}
            actual={kpi.actual}
            target={kpi.target}
            status={kpi.status}
            percentage={kpi.achievementPercentage}
            valueFormat="percentage"
            onViewDetails={onKPIViewDetails}
          />
        ))}
      </View>
    </View>
  );
};

// ============================================================
// Initiative KPI Card (Figma Design)
// ============================================================

interface InitiativeKPICardProps {
  id?: number;
  title: string;
  actual: number | string;
  target: number | string;
  status: 'on_track' | 'at_risk' | 'behind';
  onViewDetails?: (kpiId: number) => void;
  valueFormat?: 'fraction' | 'percentage'; // 'fraction' = "5/4", 'percentage' = "% 80"
  percentage?: number; // Engine achievement % (matches donut); falls back to actual/target ratio
}

const InitiativeKPICard = ({
  id,
  title,
  actual,
  target,
  status,
  onViewDetails,
  valueFormat = 'fraction',
  percentage: percentageProp,
}: InitiativeKPICardProps) => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const getStatusColor = () => {
    switch (status) {
      case 'on_track': return '#2CA02C';
      case 'at_risk': return tokens.colors.raw.orange["500"];
      case 'behind': return tokens.colors.raw.red["500"];
      default: return '#2CA02C';
    }
  };

  const statusColor = getStatusColor();

  // Achievement percentage: prefer the engine value (matches the donut); fall back
  // to the raw actual/target ratio. Bar width is clamped to 0-100.
  const actualNum = typeof actual === 'number' ? actual : parseFloat(String(actual)) || 0;
  const targetNum = typeof target === 'number' ? target : parseFloat(String(target)) || 1;
  const ratioPct = Math.min(parseFloat(((actualNum / targetNum) * 100).toFixed(2)), 100);
  const percentage = percentageProp ?? ratioPct;
  const barWidth = Math.min(Math.max(percentage, 0), 100);

  // Format value display based on type (matching Figma exactly)
  // - percentage mode: always "% 80" (commitment rate)
  // - "2/2" for actual/target numbers
  // - "% 85" for percentages (with space)
  // - "-" for no value
  const formatDisplayValue = () => {
    if (valueFormat === 'percentage') return `% ${percentage}`;
    if (actualNum === 0 && targetNum === 0) return '-';
    if (targetNum === 100) {
      // Percentage format: "% 85"
      return `% ${actualNum}`;
    }
    // Fraction format: "5/4" or "2/2"
    return `${actualNum}/${targetNum}`;
  };

  const displayValue = formatDisplayValue();
  const hasValue = displayValue !== '-';

  return (
    <View style={[cardStyles.initiativeKPICard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* KPI Title */}
      <View style={{ flex: 1 }}>
        <Text style={[cardStyles.initiativeKPICardTitle, { color: colors.textPrimary }]}>{title}</Text>
      </View>

      {/* Value and Progress Row */}
      <View style={cardStyles.initiativeKPIValueRow}>
        {/* Value Badge */}
        <View style={[cardStyles.initiativeKPIValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[cardStyles.initiativeKPIValue, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        {/* Progress Bar with Dot */}
        <View style={cardStyles.initiativeKPIProgressContainer}>
          <View style={[cardStyles.initiativeKPIProgressTrack, { backgroundColor: colors.backgroundAlt }]}>
            {hasValue && (
              <>
                <View style={[
                  cardStyles.initiativeKPIProgressFill,
                  { width: `${barWidth}%`, backgroundColor: statusColor }
                ]} />
                <View style={[
                  cardStyles.initiativeKPIProgressDot,
                  {
                    backgroundColor: statusColor,
                    end: `${barWidth}%`,
                    transform: [{ translateX: 7 }],
                    borderColor: colors.card,
                  }
                ]} />
              </>
            )}
          </View>
        </View>
      </View>

      {/* View Details Link */}
      <TouchableOpacity
        style={cardStyles.initiativeKPIDetailsLink}
        onPress={() => id && onViewDetails?.(id)}
        disabled={!id}
      >
        <ChevronLeftIcon size={16} color={id ? colors.iconDefault : colors.textMuted} />
        <Text style={[cardStyles.initiativeKPIDetailsText, { color: id ? colors.textSecondary : colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Pagination Component (Figma Design)
// ============================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <View style={[paginationStyles.container, { flexDirection: rtlRow }]}>
      {/* Previous: السابق > */}
      <TouchableOpacity
        style={[paginationStyles.navButton, isFirstPage && paginationStyles.navButtonDisabled]}
        onPress={() => !isFirstPage && onPageChange(currentPage - 1)}
        disabled={isFirstPage}
      >
        <Text style={[paginationStyles.navButtonText, isFirstPage && paginationStyles.navButtonTextDisabled]}>
          {t('common.previous')}
        </Text>
        <ChevronRightIcon size={13} color={isFirstPage ? '#94A3B8' : tokens.colors.raw.slate["700"]} />
      </TouchableOpacity>

      {/* Page Numbers */}
      {(() => {
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      })().map((page) => (
        <TouchableOpacity
          key={page}
          style={[
            paginationStyles.pageButton,
            currentPage === page && paginationStyles.pageButtonActive
          ]}
          onPress={() => onPageChange(page)}
        >
          <Text style={[
            paginationStyles.pageButtonText,
            currentPage === page && paginationStyles.pageButtonTextActive
          ]}>
            {page}
          </Text>
        </TouchableOpacity>
      ))}

      {/* Next: < التالي */}
      <TouchableOpacity
        style={[paginationStyles.navButton, isLastPage && paginationStyles.navButtonDisabled]}
        onPress={() => !isLastPage && onPageChange(currentPage + 1)}
        disabled={isLastPage}
      >
        <ChevronLeftIcon size={13} color={isLastPage ? '#94A3B8' : tokens.colors.raw.slate["700"]} />
        <Text style={[paginationStyles.navButtonText, isLastPage && paginationStyles.navButtonTextDisabled]}>
          {t('common.next')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

// Pagination Styles
const getPaginationStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["2"],
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
  navButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.slate["700"],
    textAlign: 'center',
  },
  navButtonTextDisabled: {
    color: '#94A3B8',
  },
  pageButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    minHeight: 36,
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  pageButtonActive: {
    backgroundColor: COLORS.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  pageButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.slate["700"],
    textAlign: 'center',
  },
  pageButtonTextActive: {
    color: tokens.colors.semantic.general.foreground,
  },
});

const paginationStyles = getPaginationStyles(true);

// ============================================================
// Initiative Card Component
// ============================================================

interface InitiativeCardProps {
  title: string;
  goal: string;
  progress: number;
  status: 'completed' | 'in_progress' | 'pending';
  budget?: number;
  startDate?: string;
  endDate?: string;
}

const InitiativeCard = ({
  title,
  goal,
  progress,
  status,
  budget,
  startDate,
  endDate
}: InitiativeCardProps) => {
  const { t } = useLanguage();
  const getStatusInfo = () => {
    switch (status) {
      case 'completed': return { color: tokens.colors.raw.green["500"], bg: '#F0FDF4', label: t('status.completed') };
      case 'in_progress': return { color: tokens.colors.raw.blue["500"], bg: '#EFF6FF', label: t('status.inProgress') };
      case 'pending': return { color: tokens.colors.raw.yellow["500"], bg: '#FEFCE8', label: t('status.pending') };
      default: return { color: tokens.colors.raw.gray["500"], bg: tokens.colors.raw.gray["50"], label: '-' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <View style={cardStyles.initiativeCard}>
      {/* Header */}
      <View style={cardStyles.initiativeHeader}>
        <View style={[cardStyles.statusBadge, { backgroundColor: statusInfo.bg }]}>
          <Text style={[cardStyles.statusBadgeText, { color: statusInfo.color }]}>
            {statusInfo.label}
          </Text>
        </View>
        <Text style={cardStyles.initiativeTitle}>{title}</Text>
      </View>

      {/* Goal */}
      <Text style={cardStyles.initiativeGoal}>{t('dashboard.goal')}: {goal}</Text>

      {/* Progress */}
      <View style={cardStyles.initiativeProgressRow}>
        <Text style={cardStyles.initiativeProgressText}>%{progress}</Text>
        <View style={cardStyles.initiativeProgressBar}>
          <MiniProgressBar percentage={progress} color={statusInfo.color} />
        </View>
      </View>

      {/* Footer */}
      {(startDate || endDate || budget) && (
        <View style={cardStyles.initiativeFooter}>
          {budget && (
            <Text style={cardStyles.initiativeInfo}>{t('dashboard.budget')}: {budget.toLocaleString()} {t('currency.aed')}</Text>
          )}
          {(startDate || endDate) && (
            <Text style={cardStyles.initiativeInfo}>
              {startDate} - {endDate}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

// ============================================================
// Section Header Component
// ============================================================

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  showFilter?: boolean;
}

const SectionHeader = ({ title, subtitle, showFilter }: SectionHeaderProps) => {
  const { t } = useLanguage();
  return (
    <View style={cardStyles.sectionHeader}>
      {showFilter && (
        <TouchableOpacity style={cardStyles.filterBtn}>
          <ChevronDownIcon size={16} color={tokens.colors.raw.gray["500"]} />
          <Text style={cardStyles.filterBtnText}>{t('common.filter')}</Text>
        </TouchableOpacity>
      )}
      <View style={cardStyles.sectionTitleContainer}>
        <Text style={cardStyles.sectionTitle}>{title}</Text>
        {subtitle && <Text style={cardStyles.sectionSubtitle}>{subtitle}</Text>}
      </View>
    </View>
  );
};

// ============================================================
// Filter Dropdowns Row Component
// ============================================================

interface FilterOption {
  label: string;
  value: string;
}

interface FilterDropdownProps {
  label: string;
  options?: FilterOption[];
}

const FilterDropdown = ({ label }: FilterDropdownProps) => (
  <TouchableOpacity style={cardStyles.filterDropdown}>
    <ChevronDownIcon size={14} color={tokens.colors.raw.gray["500"]} />
    <Text style={cardStyles.filterDropdownText}>{label}</Text>
  </TouchableOpacity>
);

const FiltersRow = () => {
  const { t } = useLanguage();
  return (
    <View style={cardStyles.filtersRow}>
      <FilterDropdown label={t('filters.all')} />
      <FilterDropdown label={t('dashboard.filterDateTimeScale')} />
      <FilterDropdown label={t('dashboard.filterAllStrategicGoals')} />
    </View>
  );
};

// Card Styles
const getCardStyles = (isRTL: boolean) => StyleSheet.create({
  // Goal Indicator Card
  goalCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    minWidth: 280,
    flex: 1,
    overflow: 'hidden',
  },
  goalCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  goalCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  goalGaugeContainer: {
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["3"],
  },
  goalGaugeLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["2"],
  },
  goalDescription: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 18,
    marginBottom: tokens.spacing.absolute["3"],
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  progressLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    width: 40,
  },
  countRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["3"],
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["100"],
  },
  // KPI Card Styles
  kpiCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    flex: 1,
    minWidth: 200,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 21,
  },
  kpiValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
  },
  kpiValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: 100,
  },
  kpiValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.raw.gray["900"],
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  kpiProgressTrack: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
  },
  kpiProgressFill: {
    position: 'absolute',
    end: 0,
    top: 0,
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiProgressDot: {
    position: 'absolute',
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
    top: -2,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  kpiStatusDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  kpiProgressBar: {
    marginBottom: tokens.spacing.absolute["3"],
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["1"],
  },
  kpiDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Goal Section Styles
  goalSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  goalSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["4"],
  },
  goalLeftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  goalViewDetailsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  goalViewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  goalPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  goalPercentageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  goalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  goalSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  goalStatusDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  kpiCardsGrid: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  // Strategic Goals Section Header
  strategicGoalsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["5"],
  },
  overallPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  overallPercentageText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.raw.green["500"],
  },
  strategicGoalsTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  strategicGoalsSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  strategicGoalsIcon: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-16"],
    backgroundColor: tokens.colors.raw.gray["100"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Initiative Card
  initiativeCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    flex: 1,
    minWidth: 280,
    overflow: 'hidden',
  },
  initiativeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  statusBadge: {
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  statusBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.medium,
  },
  initiativeTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
    flex: 1,
    textAlign: getTextAlign(isRTL),
  },
  initiativeGoal: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
  },
  initiativeProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  initiativeProgressBar: {
    flex: 1,
  },
  initiativeProgressText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    width: 40,
  },
  initiativeFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["3"],
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["100"],
  },
  initiativeInfo: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
  },
  // Section Header
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["6"],
  },
  sectionTitleContainer: {
    alignItems: 'flex-end',
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  sectionSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    backgroundColor: tokens.colors.raw.gray["50"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  filterBtnText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  // Filter Dropdowns
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: tokens.colors.raw.gray["50"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  filterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
  },
  // ============================================================
  // Initiative KPIs Section Styles (Figma Design)
  // ============================================================
  initiativeKPIsCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["5"],
  },
  initiativeKPIsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  initiativeKPIsPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  initiativeKPIsTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  initiativeKPIsSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    lineHeight: 24,
  },
  // Initiative Row Styles
  initiativeKPIRow: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  initiativeKPIHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  initiativeKPILeftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },
  initiativeKPIViewDetailsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  initiativeKPIViewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  initiativeKPIPercentageGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  initiativeKPIPercentageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  initiativeKPITitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
  },
  initiativeKPIRowTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
    lineHeight: 20,
  },
  initiativeKPIBullet: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.semantic.general.foreground,
  },
  // Initiative KPI Cards Grid (RTL - cards align to right)
  initiativeKPICardsGrid: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["5"],
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  // Initiative KPI Card (flex to fill space evenly)
  initiativeKPICard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    flex: 1,
    minWidth: 180,
    maxWidth: '24%',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  initiativeKPICardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  initiativeKPIValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  initiativeKPIValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  initiativeKPIValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  initiativeKPIProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  initiativeKPIProgressTrack: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
  },
  initiativeKPIProgressFill: {
    position: 'absolute',
    end: 0,
    top: 0,
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  initiativeKPIProgressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    top: -3,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  initiativeKPIDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    minHeight: 24,
  },
  initiativeKPIDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
});

const cardStyles = getCardStyles(true);

// ============================================================
// Main Dashboard Screen
// ============================================================

export default function DashboardScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<any>();
  const { user } = useAuth();
  // strategy_team / executive / superuser can inspect any org's dashboard by
  // passing `organizationId` in route.params. org_employee / org_executive
  // keep the default behaviour of showing their own org.
  const overrideOrgId = route.params?.organizationId as number | undefined;
  const isInspectingOtherOrg = typeof overrideOrgId === 'number';
  const effectiveOrgId = overrideOrgId ?? user?.profile?.organization;
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { translate, translateOrg } = useAutoTranslate();

  // Dynamic theme styles for cards
  const themedCardStyles = {
    card: {
      backgroundColor: colors.card,
      borderColor: colors.cardBorder,
    },
    cardTitle: {
      color: colors.textPrimary,
    },
    cardValue: {
      color: colors.text,
    },
    cardDescription: {
      color: colors.textMuted,
    },
    sectionTitle: {
      color: colors.textPrimary,
    },
    pageTitle: {
      color: colors.gray900,
    },
    pageSubtitle: {
      color: colors.textMuted,
    },
  };

  // REDIRECT: Executive/strategy_team users normally land on StrategyDashboard,
  // but when they click into a specific org (organizationId in route.params)
  // they're explicitly inspecting that org — skip the redirect.
  const userType = user?.profile?.user_type;
  useEffect(() => {
    if (isInspectingOtherOrg) return;
    if (
      userType === 'executive'
      || userType === 'strategy_team'
      || userType === 'superuser'
      || userType === 'performance_evaluator'
      || userType === 'evaluation_approver'
    ) {
      console.log('🔀 REDIRECT: Wrong dashboard for', userType, '-> Redirecting to StrategyStack');
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'StrategyStack' as any }],
        })
      );
    }
  }, [userType, navigation, isInspectingOtherOrg]);

  // Responsive breakpoints (using hook values)
  const isSmallScreen = isMobile;

  // Dashboard data
  const {
    data,
    isLoading: dashboardLoading,
    isFetching: dashboardFetching,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useDashboardQuery(user?.id);

  // Organization data (uses overridden orgId when strategy_team inspects a
  // specific org via route.params; falls back to the logged-in user's org).
  const {
    data: organization,
    refetch: refetchOrganization,
  } = useOrganizationQuery(effectiveOrgId);

  const canEditBanner =
    user?.profile?.user_type === 'org_employee' &&
    user?.profile?.organization === organization?.id &&
    organization?.data_entry_open === true;

  const handleEditBanner = useCallback(() => {
    // EditOrganizationBanner is registered inside MyOrganizationStack.
    // From DashboardStack we hop to the Drawer's sibling stack via nested nav.
    navigation.navigate(
      'MyOrganizationStack' as any,
      { screen: 'EditOrganizationBanner' } as any,
    );
  }, [navigation]);

  // Goals with KPIs — scoped to the inspected org. Without this scope,
  // strategy_team / executive viewing /dashboard/:id saw every org's goals
  // and KPIs mixed into the lists.
  const {
    data: goalsData,
    refetch: refetchGoals,
  } = useDashboardGoalsQuery(effectiveOrgId);

  const goals = goalsData?.goals || [];
  const goalKpis = goalsData?.kpis || {};

  // Initiatives with KPIs — same scope as goals above.
  const {
    data: initiativesData,
    refetch: refetchInitiatives,
  } = useDashboardInitiativesQuery(effectiveOrgId);

  const initiatives = initiativesData?.initiatives || [];
  const initiativeKpis = initiativesData?.kpis || {};

  // Pagination state
  const [initiativesPage, setInitiativesPage] = useState(1);
  const INITIATIVES_PER_PAGE = 4;
  const [goalsPage, setGoalsPage] = useState(1);
  const GOALS_PER_PAGE = 6;
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Due KPI Readings — page + filter state (data itself lives in React Query
  // below so it caches across remounts and back-navigation shows instantly).
  const [dueReadingsPage, setDueReadingsPage] = useState(1);
  const [dueReadingsFrequencyFilter, setDueReadingsFrequencyFilter] = useState('');
  const [dueReadingsPendingFilter, setDueReadingsPendingFilter] = useState('');
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [dueReadingsUnitFilter, setDueReadingsUnitFilter] = useState('');
  const [dueReadingsPendingUnitFilter, setDueReadingsPendingUnitFilter] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [dueReadingsKpiTypeFilter, setDueReadingsKpiTypeFilter] = useState('');
  const [dueReadingsPendingKpiTypeFilter, setDueReadingsPendingKpiTypeFilter] = useState('');
  const [showKpiTypeDropdown, setShowKpiTypeDropdown] = useState(false);
  const [dueReadingsStatusFilter, setDueReadingsStatusFilter] = useState('');
  const [dueReadingsPendingStatusFilter, setDueReadingsPendingStatusFilter] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const PAGE_SIZE = 20;

  const closeAllDropdowns = () => {
    setShowFrequencyDropdown(false);
    setShowUnitDropdown(false);
    setShowKpiTypeDropdown(false);
    setShowStatusDropdown(false);
  };

  // Org units dropdown — cached so back-navigation doesn't refetch.
  const { data: orgUnitsData } = useQuery({
    queryKey: ['orgUnits', 'dashboardDropdown', effectiveOrgId ?? null],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: 100 };
      // Scope the units dropdown to the inspected org — otherwise
      // strategy_team on /dashboard/:id gets every org's units in the
      // due-readings filter.
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      const response = await organizationalUnitsApi.list(params);
      return response.results.map((u: any) => ({ id: u.id, name: u.name }));
    },
    staleTime: 10 * 60 * 1000,
  });
  const orgUnits = orgUnitsData ?? [];

  // Due KPI readings — pulled into React Query so navigating away and back
  // (e.g. /dashboard/:id → /dashboard/services → ←) renders cached rows
  // instantly instead of flashing the "loading..." text in the table.
  // selectedYear is part of the key so a year switch refetches even though
  // the axios interceptor is what actually attaches the year param.
  const dueReadingsQuery = useQuery({
    queryKey: [
      'dueReadings',
      {
        orgId: effectiveOrgId ?? null,
        year: selectedYear ?? null,
        page: dueReadingsPage,
        frequency: dueReadingsFrequencyFilter || null,
        unit: dueReadingsUnitFilter || null,
        kpiType: dueReadingsKpiTypeFilter || null,
        status: dueReadingsStatusFilter || null,
      },
    ],
    queryFn: async () => {
      const params: Record<string, any> = { page: dueReadingsPage };
      if (dueReadingsFrequencyFilter) params.frequency = dueReadingsFrequencyFilter;
      if (dueReadingsUnitFilter) params.organizational_unit = dueReadingsUnitFilter;
      if (dueReadingsKpiTypeFilter) params.kpi_type = dueReadingsKpiTypeFilter;
      if (dueReadingsStatusFilter) params.status = dueReadingsStatusFilter;
      // Scope due readings to the inspected org so strategy_team /
      // executive viewing /dashboard/:id don't see a mix of every org.
      if (effectiveOrgId) params.organization = effectiveOrgId;
      const response = await schedulesApi.dueReadings(params);
      // Filter out archived (deleted) KPIs — backend marks them with is_archived
      const activeReadings = response.results.filter((r: any) => !(r as any).is_archived);
      const archivedCount = response.results.length - activeReadings.length;
      const totalCount = Math.max(0, response.count - archivedCount);
      return {
        readings: activeReadings,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
      };
    },
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
  });

  const dueReadings: DueKPIReading[] = dueReadingsQuery.data?.readings ?? [];
  const dueReadingsTotalCount = dueReadingsQuery.data?.totalCount ?? 0;
  const dueReadingsTotalPages = dueReadingsQuery.data?.totalPages ?? 1;
  // Only show the inline "loading..." row on the first fetch (no cached
  // data yet) — background refetches keep the previous rows visible so
  // the table doesn't flash when filters/pages change or when the user
  // navigates back to the dashboard.
  const dueReadingsLoading = dueReadingsQuery.isLoading && !dueReadingsQuery.data;

  const refetchDueReadings = useCallback(
    () => dueReadingsQuery.refetch(),
    [dueReadingsQuery],
  );

  // Navigate to Goal Detail WITHIN DashboardStack so Back returns to
  // /dashboard/:id instead of jumping to GoalsStack. When inspecting a
  // specific org, thread its id/name + 'dashboard' source so the detail
  // breadcrumb points back to that org's dashboard.
  const handleGoalViewDetails = (goalId: number) => {
    if (isInspectingOtherOrg && effectiveOrgId) {
      navigation.navigate('GoalDetail' as any, {
        id: goalId,
        organizationId: effectiveOrgId,
        organizationName: organization?.name,
        orgNavSource: 'dashboard',
      });
    } else {
      navigation.navigate('GoalDetail' as any, { id: goalId });
    }
  };

  // Navigate to KPI Detail WITHIN DashboardStack (see handleGoalViewDetails).
  const handleKPIViewDetails = (kpiId: number) => {
    if (isInspectingOtherOrg && effectiveOrgId) {
      navigation.navigate('KPIDetail' as any, {
        id: kpiId,
        organizationId: effectiveOrgId,
        organizationName: organization?.name,
        orgNavSource: 'dashboard',
      });
    } else {
      navigation.navigate('KPIDetail' as any, { id: kpiId });
    }
  };

  // Navigate to Initiative Detail screen
  const handleInitiativeViewDetails = (initiativeId: number) => {
    navigation.navigate('InitiativesStack' as any, {
      screen: 'InitiativeDetail',
      params: { id: initiativeId },
    });
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      refetchDashboard(),
      refetchOrganization(),
      refetchGoals(),
      refetchInitiatives(),
      refetchDueReadings(),
    ]);
    setRefreshing(false);
  }, [refetchDashboard, refetchOrganization, refetchGoals, refetchInitiatives, refetchDueReadings]);

  // Show skeleton on initial load (no cached data)
  if (dashboardLoading && !data) {
    return (
      <PageContainer>
        <Navbar />
        <DashboardSkeleton />
      </PageContainer>
    );
  }

  if (dashboardError) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={dashboardError.message || t('common.loadingError')}
        actionLabel={t('common.refresh')}
        onAction={refetchDashboard}
      />
    );
  }

  const stats = data?.statistics;
  const orgName = translateOrg(organization?.name || user?.profile?.organization_name) || '—';

  // Route params shared by every "View Details" / stat card — threads the
  // inspected org id + name into the list screens so they filter correctly
  // and the breadcrumb shows the right context. undefined for org_employee
  // on their own /dashboard (list screens fall back to SSO-scoped defaults).
  const listNavParams = effectiveOrgId
    ? { organizationId: effectiveOrgId, organizationName: organization?.name }
    : undefined;

  // Org-specific counts take priority when inspecting a particular org;
  // /dashboard/ aggregate stats are the fallback for org_employee.
  const orgStatsCounts = {
    goals: isInspectingOtherOrg
      ? (organization?.strategic_goals_count ?? organization?.total_goals ?? 0)
      : (stats?.total_goals ?? 0),
    kpis: isInspectingOtherOrg
      ? (organization?.kpis_count ?? organization?.total_kpis ?? 0)
      : (stats?.total_kpis ?? 0),
    initiatives: isInspectingOtherOrg
      ? (organization?.initiatives_count ?? organization?.total_initiatives ?? 0)
      : (stats?.total_initiatives ?? 0),
    completedInitiatives: isInspectingOtherOrg
      ? (organization?.initiatives_by_status?.completed ?? 0)
      : ((stats as any)?.initiatives_by_status?.completed ?? (stats as any)?.completed_initiatives ?? 0),
  };

  return (
    <PageContainer>
      {/* Unified Navbar */}
      <Navbar />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { padding: containerPadding, minHeight: 800 }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.dashboard') },
          ]}
        />

        {organization ? (
          <OrganizationBanner
            organization={organization as any}
            canEdit={Boolean(canEditBanner)}
            onEditPress={handleEditBanner}
          />
        ) : null}

        {/* Page Title */}
        <View style={styles.pageTitleSection}>
          <Text style={[styles.pageTitle, themedCardStyles.pageTitle, { fontSize: isMobile ? 20 : 24 }]}>{t('dashboard.orgEmployeeDashboard')}</Text>
          <Text style={[styles.pageSubtitle, themedCardStyles.pageSubtitle]}>
            {t('dashboard.welcome')} {user?.profile?.user_type === 'org_employee' ? t('userTypes.orgEmployee') : translate(user?.full_name)} - {orgName}
          </Text>
        </View>

        {/* Main Content Row */}
        <View style={[
          styles.mainContent,
          { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 20 }
        ]}>
          {/* Left Column: KPI Gauges */}
          <View style={[
            styles.leftColumn,
            isMobile && { minWidth: 0, width: '100%', gap: tokens.spacing.absolute["3"] }
          ]}>
            {/* KPI Card 1: Vision Achievement Commitment (مؤشر الالتزام) */}
            <View
              style={[styles.kpiGaugeCard, themedCardStyles.card]}
            >
              <View style={styles.kpiGaugeCardBody}>
                <CircularGauge
                  percentage={organization?.commitment_index ?? 0}
                  size={120}
                  strokeWidth={12}
                  color={commitmentColor(organization?.commitment_index ?? 0)}
                />
                <View style={styles.kpiGaugeTextSection}>
                  <Text style={[styles.kpiGaugeTitle, themedCardStyles.cardTitle]}>{t('dashboard.commitmentIndex')}</Text>
                  <Text style={[styles.kpiGaugeDescription, themedCardStyles.cardDescription]}>{t('dashboard.commitmentIndexDescription')}</Text>
                </View>
              </View>
            </View>

            {/* KPI Card 2: Vision Achievement (مؤشر تحقيق الرؤية) */}
            <View
              style={[styles.kpiGaugeCard, themedCardStyles.card]}
            >
              <View style={styles.kpiGaugeCardBody}>
                <SegmentedGauge
                  actual={parseFloat((Number(organization?.stage_actual_progress ?? 0) * 100).toFixed(2))}
                  target={parseFloat((Number(organization?.stage_required_progress ?? 0) * 100).toFixed(2))}
                  size={120}
                  color={commitmentColor(organization?.commitment_index ?? 0)}
                />
                <View style={styles.kpiGaugeTextSection}>
                  <Text style={[styles.kpiGaugeTitle, themedCardStyles.cardTitle]}>{t('dashboard.visionAchievementIndex')}</Text>
                  <View style={styles.legendItems}>
                    <View style={styles.legendRow}>
                      <Text style={[styles.legendRowValue, themedCardStyles.cardValue]}>% {formatPct(Number(organization?.stage_required_progress ?? 0) * 100)}</Text>
                      <View style={styles.legendRowContent}>
                        <Text style={[styles.legendRowLabel, themedCardStyles.cardDescription]}>{t('dashboard.targetRequiredPercentage')}</Text>
                        <TargetIcon size={20} color={colors.textSecondary} />
                      </View>
                    </View>
                    <View style={styles.legendRow}>
                      <Text style={[styles.legendRowValue, themedCardStyles.cardValue]}>% {formatPct(Number(organization?.stage_actual_progress ?? 0) * 100)}</Text>
                      <View style={styles.legendRowContent}>
                        <Text style={[styles.legendRowLabel, themedCardStyles.cardDescription]}>{t('dashboard.actualAchieved')}</Text>
                        <FlagIcon size={20} color={colors.textSecondary} />
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right Column: Organization Info */}
          <View style={[
            styles.rightColumn,
            isMobile && { minWidth: 0, width: '100%' }
          ]}>
            <View style={[styles.orgCard, themedCardStyles.card]}>
              {/* Org Header */}
              <View style={[styles.orgHeader, userType === 'org_executive' && { justifyContent: 'flex-end' }]}>
                {userType !== 'org_executive' && (
                  <IconButton
                    variant="secondary"
                    onPress={() => organization?.id && navigation.navigate('EditOrganization' as never, { organizationId: organization.id } as never)}
                    icon={<EditIcon />}
                    accessibilityLabel={t('common.edit')}
                  />
                )}
                <Text style={[styles.orgName, themedCardStyles.cardTitle]}>{t('dashboard.aboutEntity')}</Text>
              </View>

              {/* Content Section */}
              <View style={styles.orgContent}>
                {/* Vision */}
                <Text style={[styles.orgSectionText, themedCardStyles.cardDescription]}>
                  <Text style={[styles.orgSectionTitle, themedCardStyles.cardTitle]}>{t('dashboard.ourVision')}:{'\n'}</Text>
                  {translate(organization?.vision) || t('dashboard.visionNotSet')}
                </Text>

                {/* Mission */}
                <Text style={[styles.orgSectionText, themedCardStyles.cardDescription]}>
                  <Text style={[styles.orgSectionTitle, themedCardStyles.cardTitle]}>{t('dashboard.ourMission')}:{'\n'}</Text>
                  {translate(organization?.mission) || t('dashboard.missionNotSet')}
                </Text>

                {/* About */}
                <Text style={[styles.orgSectionText, themedCardStyles.cardDescription]}>
                  <Text style={[styles.orgSectionTitle, themedCardStyles.cardTitle]}>{t('dashboard.aboutOrganization')}:{'\n'}</Text>
                  {translate(organization?.description) || t('dashboard.aboutNotSet')}
                </Text>

                {/* Values */}
                <Text style={[styles.orgSectionText, themedCardStyles.cardDescription]}>
                  <Text style={[styles.orgSectionTitle, themedCardStyles.cardTitle]}>{t('dashboard.ourValues')}:{'\n'}</Text>
                  {translate(organization?.values) || t('dashboard.valuesNotSet')}
                </Text>
              </View>

              {/* Stats Row */}
              <View style={styles.orgStatsRow}>
                <View style={[styles.orgStatPill, { backgroundColor: isDark ? colors.gray100 : tokens.colors.raw.slate["100"] }]}>
                  <Text style={[styles.orgStatPillValue, themedCardStyles.cardValue]}>{organization?.founding_year || '-'}</Text>
                  <Text style={[styles.orgStatPillLabel, themedCardStyles.cardDescription]}>{t('dashboard.foundingYear')}</Text>
                </View>
                <View style={[styles.orgStatPill, { backgroundColor: isDark ? colors.gray100 : tokens.colors.raw.slate["100"] }]}>
                  <Text style={[styles.orgStatPillValue, themedCardStyles.cardValue]}>{organization?.employees_count ?? 0}</Text>
                  <Text style={[styles.orgStatPillLabel, themedCardStyles.cardDescription]}>{t('dashboard.humanCapital')}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Bottom Stats Cards */}
        <View style={[
          styles.statsRow,
          {
            flexDirection: isMobile ? 'column' : 'row-reverse',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            gap: isMobile ? 12 : 20,
          }
        ]}>
          {/* These four are info-only summary tiles — intentionally NOT
              clickable. Drill-down happens from the dedicated cards below
              (human capital / unit goals / services / processes). */}

          {/* Card 1: Strategic Goals */}
          <View
            style={[
              styles.statCard,
              themedCardStyles.card,
              { minWidth: isMobile ? 0 : isTablet ? 180 : 200, width: isMobile ? '100%' : 'auto', minHeight: SKELETON_DIMENSIONS.STAT_CARD_HEIGHT }
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, themedCardStyles.cardTitle]}>{t('goals.strategicGoals')}</Text>
              <GoalIcon size={isMobile ? 32 : 40} color={colors.textSecondary} />
            </View>
            {/* Org-specific counts when inspecting a particular org (strategy_team
                at /dashboard/:id); fall back to /dashboard/ aggregate otherwise. */}
            <Text style={[styles.statCardValue, themedCardStyles.cardValue, { fontSize: isMobile ? 28 : 36 }]}>{orgStatsCounts.goals ?? 0}</Text>
          </View>

          {/* Card 2: Strategic Indicators */}
          <View
            style={[
              styles.statCard,
              themedCardStyles.card,
              { minWidth: isMobile ? 0 : isTablet ? 180 : 200, width: isMobile ? '100%' : 'auto', minHeight: SKELETON_DIMENSIONS.STAT_CARD_HEIGHT }
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, themedCardStyles.cardTitle]}>{t('kpis.strategicKPIs')}</Text>
              <ChartColumnBigIcon size={isMobile ? 32 : 40} color={colors.textSecondary} />
            </View>
            <Text style={[styles.statCardValue, themedCardStyles.cardValue, { fontSize: isMobile ? 28 : 36 }]}>{orgStatsCounts.kpis ?? 0}</Text>
          </View>

          {/* Card 3: Initiatives */}
          <View
            style={[
              styles.statCard,
              themedCardStyles.card,
              { minWidth: isMobile ? 0 : isTablet ? 180 : 200, width: isMobile ? '100%' : 'auto', minHeight: SKELETON_DIMENSIONS.STAT_CARD_HEIGHT }
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, themedCardStyles.cardTitle]}>{t('initiatives.title')}</Text>
              <RocketIcon size={isMobile ? 32 : 40} color={colors.textSecondary} />
            </View>
            <Text style={[styles.statCardValue, themedCardStyles.cardValue, { fontSize: isMobile ? 28 : 36 }]}>{orgStatsCounts.initiatives ?? 0}</Text>
          </View>

          {/* Card 4: Completed Initiatives */}
          <View
            style={[
              styles.statCard,
              themedCardStyles.card,
              { minWidth: isMobile ? 0 : isTablet ? 180 : 200, width: isMobile ? '100%' : 'auto', minHeight: SKELETON_DIMENSIONS.STAT_CARD_HEIGHT }
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, themedCardStyles.cardTitle]}>{t('initiatives.completed')}</Text>
              <BadgeCheckIcon size={isMobile ? 32 : 40} color={colors.textSecondary} />
            </View>
            <Text style={[styles.statCardValue, themedCardStyles.cardValue, { fontSize: isMobile ? 28 : 36 }]}>{orgStatsCounts.completedInitiatives ?? 0}</Text>
          </View>

          {/* Card 5 (org_executive + executive): Overall evaluation level — Figma 3639:223416 */}
          {(userType === 'org_executive' || userType === 'executive') && effectiveOrgId && (
            <View
              style={{
                minWidth: isMobile ? 0 : isTablet ? 180 : 200,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <EvalSummaryCard departmentId={effectiveOrgId} />
            </View>
          )}
        </View>

        {/* ============================================================ */}
        {/* مؤشرات الأهداف الاستراتيجية - Strategic Goals Indicators */}
        {/* ============================================================ */}
        {goals.length > 0 ? (() => {
          // Pagination logic for goals
          const totalGoalsPages = Math.ceil(goals.length / GOALS_PER_PAGE);
          const goalsStartIndex = (goalsPage - 1) * GOALS_PER_PAGE;
          const paginatedGoals = goals.slice(goalsStartIndex, goalsStartIndex + GOALS_PER_PAGE);

          // Use the same vision actual progress value (stage_actual_progress) for consistency
          // with the "مؤشر تحقيق الرؤية" gauge that shows القيمة الفعلية المحققة
          const overallGoalsPercentage = parseFloat((Number(organization?.stage_actual_progress ?? 0) * 100).toFixed(2));

          return (
            <View style={[cardStyles.initiativeKPIsCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
              <StrategicGoalsSectionHeader overallPercentage={overallGoalsPercentage} />

              {/* Render paginated goals from API */}
              {paginatedGoals.map((goal, index) => {
                // Convert KPIList to the format expected by GoalSection (indicator KPI cards)
                const kpisForGoal = (goalKpis[goal.id] || []).map((kpi) => ({
                  id: kpi.id,
                  title: translate(kpi.name),
                  achieved: kpi.latest_value || '0',
                  target: kpi.target_value,
                  unit: kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit),
                  achievementPercentage: kpi.achievement_percentage,
                }));

                // If no KPIs fetched yet, show empty array (no fake placeholders)
                const displayKpis = kpisForGoal;

                // Use goal's progress_percentage from API which includes ALL related KPIs
                // (direct + process + initiative KPIs) - matches GoalDetailScreen calculation
                const goalPercentage = parseFloat(Number(goal.progress_percentage ?? goal.achievement_percentage ?? 0).toFixed(2));

                const goalStatus = goalPercentage >= 80 ? 'on_track' : goalPercentage >= 50 ? 'at_risk' : 'behind';

                return (
                  <GoalSection
                    key={goal.id}
                    goalId={goal.id}
                    title={translate(goal.title)}
                    percentage={goalPercentage}
                    status={goalStatus}
                    kpis={displayKpis}
                    onViewDetails={handleGoalViewDetails}
                    onKPIViewDetails={handleKPIViewDetails}
                  />
                );
              })}

              {/* Pagination for goals */}
              {totalGoalsPages > 1 && (
                <Pagination
                  currentPage={goalsPage}
                  totalPages={totalGoalsPages}
                  onPageChange={setGoalsPage}
                />
              )}
            </View>
          );
        })() : (
          // Show empty state when no goals from API
          <View style={[cardStyles.initiativeKPIsCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
            <StrategicGoalsSectionHeader overallPercentage={0} />
            <View style={{ padding: tokens.spacing.absolute["6"], alignItems: 'center' }}>
              <Text style={{ fontSize: tokens.typography.paragraph["paragraph font size"], color: colors.textMuted, textAlign: 'center', fontFamily: FONTS.regular }}>
                {t('dashboard.noGoalsCurrently')}
              </Text>
              <Text style={{ fontSize: tokens.typography.paragraph["paragraph small font size"], color: colors.textMuted, textAlign: 'center', marginTop: tokens.spacing.absolute["2"], fontFamily: FONTS.regular }}>
                {t('dashboard.addGoalsFromPage')}
              </Text>
            </View>
          </View>
        )}

        {/* ============================================================ */}
        {/* مؤشرات المبادرات - Initiative KPIs Section (Figma Design) */}
        {/* ============================================================ */}
        {initiatives.length > 0 && (() => {
          // Pagination logic
          const totalPages = Math.ceil(initiatives.length / INITIATIVES_PER_PAGE);
          const startIndex = (initiativesPage - 1) * INITIATIVES_PER_PAGE;
          const paginatedInitiatives = initiatives.slice(startIndex, startIndex + INITIATIVES_PER_PAGE);

          return (
            <View style={[cardStyles.initiativeKPIsCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
              <InitiativeKPIsSectionHeader
                overallPercentage={(() => {
                  // Use backend-calculated weighted achievement_percentage per initiative
                  if (initiatives.length === 0) return 0;
                  const total = initiatives.reduce((sum: number, i: any) => sum + (i.achievement_percentage ?? 0), 0);
                  return parseFloat((total / initiatives.length).toFixed(2));
                })()}
              />

              {paginatedInitiatives.map((initiative) => {
                const kpis = initiativeKpis[initiative.id] || [];
                // Calculate initiative progress from KPIs
                // Use backend-calculated weighted achievement_percentage from initiative
                const initProgress = parseFloat(Number(initiative.achievement_percentage ?? 0).toFixed(2));
                // Determine status based on achievement
                const getKpiStatus = (achievement: number): 'on_track' | 'behind' | 'at_risk' => {
                  if (achievement >= 80) return 'on_track';
                  if (achievement >= 50) return 'at_risk';
                  return 'behind';
                };
                return (
                  <InitiativeKPIRow
                    key={initiative.id}
                    initiativeId={initiative.id}
                    title={translate(initiative.title) || ''}
                    percentage={initProgress}
                    kpis={kpis.map(kpi => ({
                      id: kpi.id,
                      title: translate(kpi.name) || '',
                      actual: parseFloat(kpi.latest_value || '0'),
                      target: parseFloat(kpi.target_value || '0'),
                      status: getKpiStatus(kpi.achievement_percentage || 0),
                      achievementPercentage: kpi.achievement_percentage || 0,
                    }))}
                    onViewDetails={handleInitiativeViewDetails}
                    onKPIViewDetails={handleKPIViewDetails}
                  />
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={initiativesPage}
                  totalPages={totalPages}
                  onPageChange={setInitiativesPage}
                />
              )}
            </View>
          );
        })()}

        {/* ============================================================ */}
        {/* الهيكل التنظيمي + مخطط الوحدات التنظيمية                    */}
        {/* Only shown when strategy_team / executive inspects an org    */}
        {/* via /dashboard/:id — org_employee already sees these on the  */}
        {/* "My Organization" page.                                      */}
        {/* ============================================================ */}
        {isInspectingOtherOrg && (
          <>
            {organization?.org_structure_file && (
              <View style={[cardStyles.initiativeKPIsCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                <Text style={{ fontSize: tokens.typography.paragraph["paragraph subheading font size"], fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: tokens.spacing.absolute["3"] }}>
                  {t('orgChart.orgStructure')}
                </Text>
                <View style={{ backgroundColor: colors.backgroundAlt, borderRadius: tokens.radii.absolute["radius-8"], padding: tokens.spacing.absolute["2"] }}>
                  <Image
                    source={{ uri: (() => {
                      const path = organization.org_structure_file;
                      if (!path) return '';
                      if (path.startsWith('http')) return path;
                      const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
                      return `${origin}${path}`;
                    })() }}
                    style={{ width: '100%', aspectRatio: 16 / 9 }}
                    resizeMode="contain"
                  />
                </View>
              </View>
            )}

            {effectiveOrgId && (
              <OrganizationalChart
                organizationId={effectiveOrgId}
                organizationName={translateOrg(organization?.name)}
                onNodePress={(node) => {
                  navigation.navigate('OrganizationalUnitDetail' as any, { id: node.id });
                }}
                onMoveComplete={() => refetchOrganization()}
              />
            )}
          </>
        )}

        {/* ============================================================ */}
        {/* Progress Summary Section */}
        {/* ============================================================ */}
        <SectionHeader
          title={t('dashboard.overallProgressRates')}
          subtitle={t('dashboard.progressSummaryByCategory')}
        />

        {/* Category achievement cards from organization data */}
        {(() => {
          const processesAchievement = parseFloat(Number(organization?.processes_achievement ?? 0).toFixed(2));
          const servicesAchievement = parseFloat(Number(organization?.services_achievement ?? 0).toFixed(2));
          const unitsAchievement = parseFloat(Number(organization?.units_achievement ?? 0).toFixed(2));
          const humanCapitalAchievement = parseFloat(Number(organization?.human_capital_achievement ?? 0).toFixed(2));

          const getGaugeColor = (percentage: number) => {
            if (percentage >= 80) return tokens.colors.raw.green["500"];
            if (percentage >= 50) return tokens.colors.raw.yellow["500"];
            return tokens.colors.raw.red["500"];
          };

          // listNavParams is hoisted to the component scope (see above).
          const categoryCards = [
            {
              key: 'humanCapital',
              label: t('navigation.humanCapital'),
              percentage: humanCapitalAchievement,
              icon: (
                <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                  <Circle cx={12} cy={8} r={4} stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} fill="none" />
                  <Path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" fill="none" />
                </Svg>
              ),
              // Navigate within DashboardStack (not cross-stack) so the
              // browser/native Back button pops to /dashboard/:id instead
              // of jumping to an unrelated drawer stack.
              onPress: () => navigation.navigate('Employees' as any, listNavParams),
            },
            {
              key: 'unitGoals',
              label: t('navigation.unitGoals'),
              percentage: unitsAchievement,
              icon: (
                <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                  <Path d="M6 22V4c0-.55.2-1.02.59-1.41C6.98 2.2 7.45 2 8 2h8c.55 0 1.02.2 1.41.59.39.39.59.86.59 1.41v18" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <Path d="M2 22V12c0-.55.2-1.02.59-1.41C2.98 10.2 3.45 10 4 10h2" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <Path d="M18 8h2c.55 0 1.02.2 1.41.59.39.39.59.86.59 1.41V22" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <Path d="M10 6h4M10 10h4M10 14h4M10 18h4" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" fill="none" />
                </Svg>
              ),
              onPress: () => navigation.navigate('UnitGoals' as any, listNavParams),
            },
            {
              key: 'services',
              label: t('navigation.services'),
              percentage: servicesAchievement,
              icon: (
                <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                  <Path d="M11 14H7a5 5 0 0 0-5 5v0a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v0a5 5 0 0 0-5-5h-2Z" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <Path d="M15.7 4.6a3.5 3.5 0 0 1-1.4 6.3L11.5 14" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" fill="none" />
                  <Circle cx={14} cy={7} r={1} fill={tokens.colors.semantic.general.foreground} />
                </Svg>
              ),
              onPress: () => navigation.navigate('Services' as any, listNavParams),
            },
            {
              key: 'processes',
              label: t('navigation.processes'),
              percentage: processesAchievement,
              icon: (
                <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                  <Path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <Circle cx={12} cy={13} r={2} stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} fill="none" />
                  <Path d="M12 10v1M12 15v1M14.6 11.5l-.87.5M10.27 14l-.87.5M14.6 14.5l-.87-.5M10.27 12l-.87-.5" stroke={tokens.colors.semantic.general.foreground} strokeWidth={1.5} strokeLinecap="round" fill="none" />
                </Svg>
              ),
              onPress: () => navigation.navigate('Processes' as any, listNavParams),
            },
          ];

          return (
            <View style={[
              styles.progressSummaryRow,
              {
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 12 : 20,
              }
            ]}>
              {categoryCards.map((card) => (
                <View
                  key={card.key}
                  style={[
                    styles.progressSummaryCard,
                    { minWidth: isMobile ? 0 : isTablet ? 160 : 200, width: isMobile ? '100%' : 'auto' }
                  ]}
                >
                  {/* Header: Icon + Label */}
                  <View style={styles.progressCardHeader}>
                    <Text style={styles.progressCardLabel}>{card.label}</Text>
                    <View style={styles.progressCardIcon}>{card.icon}</View>
                  </View>

                  {/* Circular Gauge */}
                  <CircularGauge
                    percentage={card.percentage}
                    size={isMobile ? 90 : 110}
                    strokeWidth={isMobile ? 10 : 12}
                    color={getGaugeColor(card.percentage)}
                  />

                  {/* View Details Link */}
                  <TouchableOpacity
                    style={styles.progressCardLink}
                    onPress={card.onPress}
                  >
                    <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                      <Path d="M15 18l-6-6 6-6" stroke={tokens.colors.semantic.general.foreground} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                    <Text style={styles.progressCardLinkText}>{t('common.viewDetails')}</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          );
        })()}

        {/* ============================================================ */}
        {/* قراءات مؤشرات مستحقة - Due KPI Readings Table */}
        {/* ============================================================ */}
        <View style={[dueReadingsStyles.card, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
          {/* Header Row */}
          <View style={dueReadingsStyles.headerRow}>
            <View style={dueReadingsStyles.headerIcons}>
              <IconButton
                variant="secondary"
                size="sm"
                icon={
                  <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
                    <Path d="M6 9V2H18V9" stroke={colors.textSecondary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18" stroke={colors.textSecondary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M18 14H6V22H18V14Z" stroke={colors.textSecondary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                }
                accessibilityLabel={t('common.print')}
                onPress={() => {}}
              />
              <IconButton
                variant="secondary"
                size="sm"
                icon={
                  <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
                    <Path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8" stroke={colors.textSecondary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M15 2H9C8.44772 2 8 2.44772 8 3V5C8 5.55228 8.44772 6 9 6H15C15.5523 6 16 5.55228 16 5V3C16 2.44772 15.5523 2 15 2Z" stroke={colors.textSecondary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                }
                accessibilityLabel={t('common.copy')}
                onPress={() => {}}
              />
            </View>
            <Text style={[dueReadingsStyles.title, { color: colors.textPrimary }]}>{t('dashboard.dueReadings')}</Text>
          </View>

          {/* Filter Row */}
          <View style={dueReadingsStyles.filterRow}>
            <TouchableOpacity
              style={[dueReadingsStyles.applyFilterButton, { borderColor: colors.cardBorder }]}
              onPress={() => {
                setDueReadingsFrequencyFilter(dueReadingsPendingFilter);
                setDueReadingsUnitFilter(dueReadingsPendingUnitFilter);
                setDueReadingsKpiTypeFilter(dueReadingsPendingKpiTypeFilter);
                setDueReadingsStatusFilter(dueReadingsPendingStatusFilter);
                setDueReadingsPage(1);
                closeAllDropdowns();
              }}
            >
              <Text style={dueReadingsStyles.applyFilterText}>{t('dashboard.applyFilter')}</Text>
            </TouchableOpacity>

            {/* Frequency Filter */}
            <View style={{ position: 'relative', zIndex: 20 }}>
              <TouchableOpacity
                style={[dueReadingsStyles.frequencyDropdown, { borderColor: colors.cardBorder }]}
                onPress={() => {
                  const wasOpen = showFrequencyDropdown;
                  closeAllDropdowns();
                  if (!wasOpen) setShowFrequencyDropdown(true);
                }}
              >
                <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                  <Path d="M6 9L12 15L18 9" stroke={colors.textMuted} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={[dueReadingsStyles.frequencyDropdownText, { color: colors.textPrimary }]}>
                  {dueReadingsPendingFilter
                    ? t(`dashboard.${dueReadingsPendingFilter === 'half_yearly' ? 'halfYearly' : dueReadingsPendingFilter}`)
                    : t('dashboard.allFrequencies')}
                </Text>
              </TouchableOpacity>

              {showFrequencyDropdown && (
                <View style={[dueReadingsStyles.dropdown, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                  {[
                    { value: '', label: t('dashboard.allFrequencies') },
                    { value: 'daily', label: t('dashboard.daily') },
                    { value: 'weekly', label: t('dashboard.weekly') },
                    { value: 'monthly', label: t('dashboard.monthly') },
                    { value: 'quarterly', label: t('dashboard.quarterly') },
                    { value: 'half_yearly', label: t('dashboard.halfYearly') },
                    { value: 'yearly', label: t('dashboard.yearly') },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        dueReadingsStyles.dropdownItem,
                        dueReadingsPendingFilter === option.value && { backgroundColor: colors.card === tokens.colors.raw.white ? tokens.colors.raw.slate["100"] : colors.gray100 },
                      ]}
                      onPress={() => {
                        setDueReadingsPendingFilter(option.value);
                        setShowFrequencyDropdown(false);
                      }}
                    >
                      <Text style={[dueReadingsStyles.dropdownItemText, { color: colors.textPrimary }]}>{option.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* Organizational Unit Filter */}
            <View style={{ position: 'relative', zIndex: 20 }}>
              <TouchableOpacity
                style={[dueReadingsStyles.frequencyDropdown, { borderColor: colors.cardBorder }]}
                onPress={() => {
                  const wasOpen = showUnitDropdown;
                  closeAllDropdowns();
                  if (!wasOpen) setShowUnitDropdown(true);
                }}
              >
                <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                  <Path d="M6 9L12 15L18 9" stroke={colors.textMuted} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={[dueReadingsStyles.frequencyDropdownText, { color: colors.textPrimary }]}>
                  {dueReadingsPendingUnitFilter
                    ? orgUnits.find(u => u.id.toString() === dueReadingsPendingUnitFilter)?.name || t('dashboard.allUnits')
                    : t('dashboard.allUnits')}
                </Text>
              </TouchableOpacity>

              {showUnitDropdown && (
                <View style={[dueReadingsStyles.dropdown, { backgroundColor: colors.card, borderColor: colors.cardBorder, maxHeight: 250 }]}>
                  <ScrollView nestedScrollEnabled>
                    <TouchableOpacity
                      style={[
                        dueReadingsStyles.dropdownItem,
                        dueReadingsPendingUnitFilter === '' && { backgroundColor: colors.card === tokens.colors.raw.white ? tokens.colors.raw.slate["100"] : colors.gray100 },
                      ]}
                      onPress={() => {
                        setDueReadingsPendingUnitFilter('');
                        setShowUnitDropdown(false);
                      }}
                    >
                      <Text style={[dueReadingsStyles.dropdownItemText, { color: colors.textPrimary }]}>{t('dashboard.allUnits')}</Text>
                    </TouchableOpacity>
                    {orgUnits.map((unit) => (
                      <TouchableOpacity
                        key={unit.id}
                        style={[
                          dueReadingsStyles.dropdownItem,
                          dueReadingsPendingUnitFilter === unit.id.toString() && { backgroundColor: colors.card === tokens.colors.raw.white ? tokens.colors.raw.slate["100"] : colors.gray100 },
                        ]}
                        onPress={() => {
                          setDueReadingsPendingUnitFilter(unit.id.toString());
                          setShowUnitDropdown(false);
                        }}
                      >
                        <Text style={[dueReadingsStyles.dropdownItemText, { color: colors.textPrimary }]}>{unit.name}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>

            {/* KPI Type Filter */}
            <View style={{ position: 'relative', zIndex: 20 }}>
              <TouchableOpacity
                style={[dueReadingsStyles.frequencyDropdown, { borderColor: colors.cardBorder }]}
                onPress={() => {
                  const wasOpen = showKpiTypeDropdown;
                  closeAllDropdowns();
                  if (!wasOpen) setShowKpiTypeDropdown(true);
                }}
              >
                <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                  <Path d="M6 9L12 15L18 9" stroke={colors.textMuted} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={[dueReadingsStyles.frequencyDropdownText, { color: colors.textPrimary }]}>
                  {dueReadingsPendingKpiTypeFilter
                    ? t(`dashboard.kpiType_${dueReadingsPendingKpiTypeFilter}`)
                    : t('dashboard.allKpiTypes')}
                </Text>
              </TouchableOpacity>

              {showKpiTypeDropdown && (
                <View style={[dueReadingsStyles.dropdown, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                  {[
                    { value: '', label: t('dashboard.allKpiTypes') },
                    { value: 'strategic', label: t('dashboard.kpiType_strategic') },
                    { value: 'operational', label: t('dashboard.kpiType_operational') },
                    { value: 'efficiency', label: t('dashboard.kpiType_efficiency') },
                    { value: 'effectiveness', label: t('dashboard.kpiType_effectiveness') },
                    { value: 'impact', label: t('dashboard.kpiType_impact') },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        dueReadingsStyles.dropdownItem,
                        dueReadingsPendingKpiTypeFilter === option.value && { backgroundColor: colors.card === tokens.colors.raw.white ? tokens.colors.raw.slate["100"] : colors.gray100 },
                      ]}
                      onPress={() => {
                        setDueReadingsPendingKpiTypeFilter(option.value);
                        setShowKpiTypeDropdown(false);
                      }}
                    >
                      <Text style={[dueReadingsStyles.dropdownItemText, { color: colors.textPrimary }]}>{option.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* Status Filter */}
            <View style={{ position: 'relative', zIndex: 20 }}>
              <TouchableOpacity
                style={[dueReadingsStyles.frequencyDropdown, { borderColor: colors.cardBorder }]}
                onPress={() => {
                  const wasOpen = showStatusDropdown;
                  closeAllDropdowns();
                  if (!wasOpen) setShowStatusDropdown(true);
                }}
              >
                <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                  <Path d="M6 9L12 15L18 9" stroke={colors.textMuted} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={[dueReadingsStyles.frequencyDropdownText, { color: colors.textPrimary }]}>
                  {dueReadingsPendingStatusFilter
                    ? t(`dashboard.status_${dueReadingsPendingStatusFilter}`)
                    : t('dashboard.allStatuses')}
                </Text>
              </TouchableOpacity>

              {showStatusDropdown && (
                <View style={[dueReadingsStyles.dropdown, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                  {[
                    { value: '', label: t('dashboard.allStatuses') },
                    { value: 'overdue', label: t('dashboard.status_overdue') },
                    { value: 'due', label: t('dashboard.status_due') },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        dueReadingsStyles.dropdownItem,
                        dueReadingsPendingStatusFilter === option.value && { backgroundColor: colors.card === tokens.colors.raw.white ? tokens.colors.raw.slate["100"] : colors.gray100 },
                      ]}
                      onPress={() => {
                        setDueReadingsPendingStatusFilter(option.value);
                        setShowStatusDropdown(false);
                      }}
                    >
                      <Text style={[dueReadingsStyles.dropdownItemText, { color: colors.textPrimary }]}>{option.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          </View>

          {/* Table Header */}
          <View style={[dueReadingsStyles.tableHeader, { borderBottomColor: colors.cardBorder }]}>
            <View style={dueReadingsStyles.colAction}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}></Text>
            </View>
            <View style={dueReadingsStyles.colStatus}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}>{t('dashboard.status')}</Text>
            </View>
            <View style={dueReadingsStyles.colDate}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}>{t('dashboard.nextReadingDate')}</Text>
            </View>
            <View style={dueReadingsStyles.colFrequency}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}>{t('dashboard.measurementFrequencyCol')}</Text>
            </View>
            <View style={dueReadingsStyles.colType}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}>{t('dashboard.type')}</Text>
            </View>
            <View style={dueReadingsStyles.colName}>
              <Text style={[dueReadingsStyles.headerText, { color: colors.textPrimary }]}>{t('dashboard.kpiName')}</Text>
            </View>
          </View>

          {/* Table Body */}
          {dueReadingsLoading ? (
            <View style={dueReadingsStyles.loadingContainer}>
              <Text style={{ color: colors.textMuted, fontFamily: FONTS.regular }}>{t('common.loading')}</Text>
            </View>
          ) : dueReadings.length === 0 ? (
            <View style={dueReadingsStyles.loadingContainer}>
              <Text style={{ color: colors.textMuted, fontFamily: FONTS.regular }}>{t('dashboard.noDueReadings')}</Text>
            </View>
          ) : (
            dueReadings.map((reading) => {
              const frequencyKey = reading.frequency === 'half_yearly' ? 'halfYearly' : reading.frequency;
              const kpiTypeColors: Record<string, string> = {
                strategic: '#7C3AED',
                operational: tokens.colors.raw.orange["500"],
                efficiency: tokens.colors.raw.blue["500"],
                effectiveness: tokens.colors.raw.green["500"],
                impact: tokens.colors.raw.yellow["500"],
              };
              const typeBadgeColor = kpiTypeColors[reading.kpi_type] || tokens.colors.raw.gray["500"];

              return (
                <TouchableOpacity
                  key={reading.id}
                  activeOpacity={0.7}
                  onPress={() => handleKPIViewDetails(reading.kpi)}
                  style={[dueReadingsStyles.tableRow, { borderBottomColor: colors.cardBorder }]}
                >
                  {/* Action */}
                  <View style={dueReadingsStyles.colAction}>
                    {userType === 'org_employee' && (
                      <TouchableOpacity
                        style={dueReadingsStyles.addReadingButton}
                        onPress={(e) => {
                          e.stopPropagation();
                          navigation.navigate('GoalsStack' as any, { screen: 'AddKPIReading', params: { kpiId: reading.kpi } });
                        }}
                      >
                        <Text style={dueReadingsStyles.addReadingText}>+ {t('dashboard.addNewReading')}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  {/* Status */}
                  <View style={dueReadingsStyles.colStatus}>
                    <View style={[dueReadingsStyles.dueBadge, reading.is_overdue && dueReadingsStyles.overdueBadge]}>
                      <Text style={[dueReadingsStyles.dueBadgeText, reading.is_overdue && dueReadingsStyles.overdueBadgeText]}>
                        {reading.is_overdue ? t('dashboard.overdue') : t('dashboard.due')}
                      </Text>
                    </View>
                  </View>
                  {/* Date */}
                  <View style={dueReadingsStyles.colDate}>
                    <Text style={[dueReadingsStyles.cellText, { color: colors.textPrimary }]}>
                      {new Date(reading.scheduled_date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Text>
                  </View>
                  {/* Frequency */}
                  <View style={dueReadingsStyles.colFrequency}>
                    <Text style={[dueReadingsStyles.cellText, { color: colors.textPrimary }]}>
                      {t(`dashboard.${frequencyKey}`)}
                    </Text>
                  </View>
                  {/* Type Badge + Source Name */}
                  <View style={dueReadingsStyles.colType}>
                    <View style={[
                      dueReadingsStyles.typeBadge,
                      { backgroundColor: typeBadgeColor },
                    ]}>
                      <Text style={dueReadingsStyles.typeBadgeText}>
                        {translate(reading.kpi_type_display || reading.kpi_type)}
                      </Text>
                    </View>
                    {reading.source_name && (
                      <Text style={[dueReadingsStyles.sourceNameText, { color: colors.textMuted }]} numberOfLines={1}>
                        {translate(reading.source_name)}
                      </Text>
                    )}
                  </View>
                  {/* KPI Name */}
                  <View style={dueReadingsStyles.colName}>
                    <Text style={[dueReadingsStyles.cellText, { color: colors.textPrimary }]} numberOfLines={2}>
                      {translate(reading.kpi_name)}
                    </Text>
                    {reading.department_name && (
                      <Text style={[dueReadingsStyles.departmentText, { color: colors.textMuted }]} numberOfLines={1}>
                        {translateOrg(reading.department_name)}
                      </Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })
          )}

          {/* Pagination Footer */}
          {dueReadingsTotalCount > 0 && (
            <View style={[dueReadingsStyles.paginationRow, { flexDirection: rtlRow }]}>
              {/* Count info - far right in RTL (first in row-reverse) */}
              <Text style={[dueReadingsStyles.countText, { color: colors.textMuted }]}>
                {t('dashboard.showing')} {((dueReadingsPage - 1) * PAGE_SIZE) + 1}-{Math.min(dueReadingsPage * PAGE_SIZE, dueReadingsTotalCount)} {t('dashboard.of')} {dueReadingsTotalCount} {t('dashboard.items')}
              </Text>

              <View style={{ flex: 1 }} />

              {/* Previous: السابق > */}
              <TouchableOpacity
                style={[dueReadingsStyles.paginationNavButton, dueReadingsPage <= 1 && dueReadingsStyles.paginationNavDisabled]}
                onPress={() => dueReadingsPage > 1 && setDueReadingsPage(dueReadingsPage - 1)}
                disabled={dueReadingsPage <= 1}
              >
                <Text style={[dueReadingsStyles.paginationNavText, dueReadingsPage <= 1 && { color: '#94A3B8' }]}>
                  {t('dashboard.previous')}
                </Text>
                <ChevronRightIcon size={13} color={dueReadingsPage <= 1 ? '#94A3B8' : tokens.colors.raw.slate["700"]} />
              </TouchableOpacity>

              {/* Page Number */}
              <View style={dueReadingsStyles.pageNumberBox}>
                <Text style={dueReadingsStyles.pageNumberText}>{dueReadingsPage}</Text>
              </View>

              {/* Next: < التالي */}
              <TouchableOpacity
                style={[dueReadingsStyles.paginationNavButton, dueReadingsPage >= dueReadingsTotalPages && dueReadingsStyles.paginationNavDisabled]}
                onPress={() => dueReadingsPage < dueReadingsTotalPages && setDueReadingsPage(dueReadingsPage + 1)}
                disabled={dueReadingsPage >= dueReadingsTotalPages}
              >
                <ChevronLeftIcon size={13} color={dueReadingsPage >= dueReadingsTotalPages ? '#94A3B8' : tokens.colors.raw.slate["700"]} />
                <Text style={[dueReadingsStyles.paginationNavText, dueReadingsPage >= dueReadingsTotalPages && { color: '#94A3B8' }]}>
                  {t('dashboard.next')}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

      </ScrollView>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["4"],
    backgroundColor: COLORS.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  headerIconBtn: {
    padding: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  notificationBadge: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -4,
    end: -4,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-10"],
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: COLORS.white,
    fontSize: 10,
    fontFamily: FONTS.bold,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  searchBtn: {
    backgroundColor: tokens.colors.raw.gray["100"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  searchBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.gray["50"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    minWidth: 280,
  },
  searchInput: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
    paddingEnd: tokens.spacing.absolute["2"],
  },
  headerTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.semiBold,
    color: tokens.colors.raw.gray["900"],
  },
  // Scroll Content
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["4"],
  },
  scrollContentSmall: {
    padding: tokens.spacing.absolute["3"],
  },
  // Breadcrumb
  breadcrumb: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  // Page Title
  pageTitleSection: {
    marginBottom: tokens.spacing.absolute["3"],
    alignItems: 'flex-end',
  },
  pageTitle: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
    marginBottom: tokens.spacing.absolute["1"],
  },
  pageSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  // Main Content
  mainContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  mainContentSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  leftColumn: {
    flex: 1,
    minWidth: 340,
    gap: tokens.spacing.absolute["5"],
  },
  leftColumnSmall: {
    minWidth: 0,
    width: '100%',
    gap: tokens.spacing.absolute["4"],
  },
  rightColumn: {
    flex: 1,
    minWidth: 400,
  },
  rightColumnSmall: {
    minWidth: 0,
    width: '100%',
  },
  // KPI Gauge Cards (Vision indicators)
  kpiGaugeCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    height: 160,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
    overflow: 'hidden',
  },
  kpiGaugeCardBody: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 40,
    flex: 1,
  },
  kpiGaugeTextSection: {
    flex: 1,
    minWidth: 180,
    gap: tokens.spacing.absolute["4"],
    alignItems: 'flex-end',
  },
  kpiGaugeTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
  },
  kpiGaugeDescription: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  legendItems: {
    width: '100%',
    gap: tokens.spacing.absolute["4"],
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    gap: tokens.spacing.absolute["2"],
  },
  legendRowValue: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  legendRowContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
  },
  legendRowLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  legendDotIndicator: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  // Organization Card
  orgCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
    gap: tokens.spacing.absolute["2"],
    overflow: 'hidden',
    flex: 1,
  },
  orgHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orgName: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  orgContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  orgSection: {
  },
  orgSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  orgSectionText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 16,
    marginBottom: tokens.spacing.absolute["1"],
  },
  orgStatsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2_5"],
    marginTop: 'auto',
  },
  orgStatPill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    gap: tokens.spacing.absolute["1"],
  },
  orgStatPillValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  orgStatPillLabel: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  orgStat: {
    alignItems: 'center',
  },
  orgStatValue: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: tokens.colors.raw.gray["900"],
  },
  orgStatLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["1"],
  },
  // Stats Cards Row
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  statsRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  // Strategic Goals Card (white container)
  strategicGoalsCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    marginTop: tokens.spacing.absolute["8"],
    marginBottom: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  demoDataNote: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center' as const,
    marginTop: tokens.spacing.absolute["4"],
  },
  statCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  statCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["500"],
  },
  statCardValue: {
    fontSize: 36,
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: tokens.colors.raw.gray["900"],
    textAlign: 'center',
  },
  // Goals Grid
  goalsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Initiatives Grid
  initiativesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Progress Summary Section
  progressSummaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  progressSummaryCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["5"],
    alignItems: 'center',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  progressCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  progressCardLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  progressCardIcon: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressCardLink: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    marginTop: tokens.spacing.absolute["1"],
  },
  progressCardLinkText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Table Styles
  tableContainer: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'hidden',
    marginBottom: tokens.spacing.absolute["6"],
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: tokens.colors.raw.gray["50"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  tableHeaderCell: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: '#FAFAFA',
  },
  tableCell: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  tableCellText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
  },
  miniProgressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    width: '100%',
  },
  miniProgressText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    minWidth: 35,
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);

// ============================================================
// Due KPI Readings Table Styles
// ============================================================
const getDueReadingsStyles = (isRTL: boolean) => StyleSheet.create({
  card: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    marginTop: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1)',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerIcons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["5"],
    flexWrap: 'wrap',
    zIndex: 10,
  },
  applyFilterButton: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    backgroundColor: COLORS.white,
  },
  applyFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.semantic.general.foreground,
  },
  frequencyDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    minWidth: 200,
    justifyContent: 'flex-end',
  },
  frequencyDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  dropdown: {
    position: 'absolute',
    top: 42,
    end: 0,
    minWidth: 200,
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    zIndex: 1000,
    elevation: 10,
    boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: tokens.spacing.absolute["px"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    marginTop: tokens.spacing.absolute["4"],
  },
  headerText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.bold,
    textAlign: getTextAlign(isRTL),
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: tokens.spacing.absolute["px"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  colName: {
    flex: 3,
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  colType: {
    flex: 1.2,
    paddingHorizontal: tokens.spacing.absolute["2"],
    alignItems: 'flex-end',
  },
  colFrequency: {
    flex: 1,
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  colDate: {
    flex: 1.5,
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  colStatus: {
    flex: 1,
    paddingHorizontal: tokens.spacing.absolute["2"],
    alignItems: 'flex-end',
  },
  colAction: {
    flex: 1.5,
    paddingHorizontal: tokens.spacing.absolute["2"],
    alignItems: 'flex-start',
  },
  cellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  departmentText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["0_5"],
  },
  typeBadge: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-20"],
  },
  typeBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.bold,
    color: COLORS.white,
    textAlign: 'center',
  },
  dueBadge: {
    backgroundColor: '#F59E0B',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-20"],
  },
  dueBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.bold,
    color: COLORS.white,
    textAlign: 'center',
  },
  overdueBadge: {
    backgroundColor: tokens.colors.raw.red["500"],
  },
  overdueBadgeText: {
    color: COLORS.white,
  },
  sourceNameText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    marginTop: tokens.spacing.absolute["1"],
    textAlign: 'center',
  },
  addReadingButton: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    backgroundColor: COLORS.white,
  },
  addReadingText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  loadingContainer: {
    padding: tokens.spacing.absolute["10"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingTop: tokens.spacing.absolute["4"],
  },
  paginationNavButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  paginationNavDisabled: {
    opacity: 0.5,
  },
  paginationNavText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["700"],
  },
  pageNumberBox: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageNumberText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["700"],
  },
  countText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
  },
});

const dueReadingsStyles = getDueReadingsStyles(true);
